import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LegacyUserForMigration } from '../../models/legacy-user.model';
import { MigrationStatus } from '../../models/migration-status.enum';
import { PaginatedResponse } from "@shared/models/paginated-response-model";
import { AutocompleteComponent } from '../../../../shared/components/autocomplete/autocomplete.component';
import { LegacyUserService } from '@features/user/services/legacy-user.service';
import { MIGRATION_CONFIG } from './user-migration.constants';
import { UserMigrationStateService } from './user-migration-state.service';
import { UserMigrationFilterService } from './user-migration-filter.service';
import { TableColumn, PaginationConfig } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';

@Component({
  selector: 'app-user-migration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    AutocompleteComponent,
    TableSurfaceComponent,
  ],
  templateUrl: './user-migration.component.html',
  styleUrl: './user-migration.component.scss'
})
export class UserMigrationComponent implements OnInit, OnDestroy {
  // Enum reference for template binding
  readonly MigrationStatus = MigrationStatus;

  // Table configuration
  columns: TableColumn[] = [
    { key: 'userInfo', header: 'Información del Usuario' },
    { key: 'accesos', header: 'Accesos', width: '180px' },
    { key: 'roles', header: 'Roles' },
    { key: 'unidad', header: 'Unidad' },
    { key: 'status', header: 'Estado' },
    { key: 'actions', header: 'Acciones', exportable: false }
  ];
  
  tableData: LegacyUserForMigration[] = [];
  isLoading = false;
  errorMessage = '';
  
  // Pagination state
  currentPage: number = 1;
  pageSize: number = MIGRATION_CONFIG.DEFAULT_PAGE_SIZE;
  totalCount: number = 0;
  paginationConfig: PaginationConfig = {
    currentPage: 1,
    pageSize: MIGRATION_CONFIG.DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0
  };

  // Observable-based filter subjects
  private readonly searchSubject$ = new Subject<string>();
  readonly searchControl = new FormControl('');
  private readonly destroy$ = new Subject<void>();

  constructor(
    private legacyUserService: LegacyUserService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    public migrationStateService: UserMigrationStateService,
    public filterService: UserMigrationFilterService
  ) {}

  // Convenience getters for template binding
  get selectedArea(): string | null {
    return this.filterService.getCurrentState().selectedArea ?? null;
  }

  get selectedDistrito(): string | null {
    return this.filterService.getCurrentState().selectedDistrito ?? null;
  }

  get selectedComunidad(): string | null {
    return this.filterService.getCurrentState().selectedComunidad ?? null;
  }

  ngOnInit(): void {
    this.initializeFilters();
    this.loadLegacyUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize filter subjects and reactive streams
   */
  private initializeFilters(): void {
    // Search debouncing
    this.searchSubject$
      .pipe(
        debounceTime(MIGRATION_CONFIG.DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchText => {
        this.filterService.setSearchText(searchText);
        this.reloadWithPaginationReset();
      });
  }

  /**
   * Load legacy users from backend
   */
  private loadLegacyUsers(): void {
    this.setLoadingState(true);
    const params = this.buildLoadParams();

    this.legacyUserService.getAllLegacyUsers(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.onLoadSuccess(response),
        error: () => this.onLoadError()
      });
  }

  /**
   * Build parameters for API request
   */
  private buildLoadParams() {
    const filterState = this.filterService.getCurrentState();
    return {
      page: this.currentPage,
      pageSize: this.pageSize,
      validOnly: false,
      searchText: filterState.searchText,
      libraryLevel: filterState.selectedLibraryLevel,
      area: filterState.selectedArea,
      distrito: filterState.selectedDistrito,
      comunidad: filterState.selectedComunidad
    };
  }



  /**
   * Handle successful data load
   */
  private onLoadSuccess(response: any): void {
    try {
      // Extract users and filter options from unified response
      const usersResponse = response?.users;
      const filterOptions = response?.filterOptions;

      if (!usersResponse?.items) {
        throw new Error(`Invalid response structure: missing users.items. Response: ${JSON.stringify(response)}`);
      }

      // Update filter options if present
      if (filterOptions) {
        this.filterService.setLibraryLevels(
          (filterOptions.accesos || []).filter((level: string) => level?.trim())
        );
        this.filterService.setHierarchicalUnits(
          (filterOptions.areas || []).filter((a: string) => a?.trim()),
          (filterOptions.distritos || []).filter((d: string) => d?.trim()),
          (filterOptions.comunidades || []).filter((c: string) => c?.trim())
        );
      }

      // Transform backend DTOs to frontend model
      // Backend sends: LegacyUserForMigrationDto (flat structure)
      // Frontend expects: LegacyUserForMigration (nested legacyData + migrationStatus)
      const transformedItems: LegacyUserForMigration[] = usersResponse.items.map((item: any) => {
        // Parse full name into first and last name
        const nameParts = (item.fullName || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        return {
          legacyData: {
            email: item.email || '',
            firstName: firstName,
            lastName: lastName,
            documentNumber: item.document || '',
            isEnabled: true,
            libraryAccesses: (item.accesses || []).map((access: string) => {
              const [id, name] = access.split(' - ');
              return {
                id: parseInt(id) || 0,
                name: name || access
              };
            }),
            unitTypeId: item.unitHierarchyInfo?.mainUnit ? 1 : undefined,
            unitId: item.unitHierarchyInfo?.mainUnit,
            unitHierarchy: item.unitHierarchyInfo?.mainUnit || '',
            unitHierarchyInfo: {
              mainUnit: item.unitHierarchyInfo?.mainUnit || '',
              district: item.unitHierarchyInfo?.district,
              area: item.unitHierarchyInfo?.area
            },
            isEditor: item.isEditor || false,
            isValidator: item.isValidator || false,
            mappedRoles: [],
            mappedMinistry: ''
          },
          migrationStatus: item.migrationStatus,
          keycloakUserId: item.userId,
          memberId: item.memberId
        };
      });
      
      this.filterService.setAllUsers(transformedItems);
      this.tableData = transformedItems;
      this.totalCount = usersResponse.totalCount || 0;
      this.updatePaginationConfig();

      this.setLoadingState(false);
      this.cdr.markForCheck();
    } catch (err) {
      this.errorMessage = 'Error al procesar los usuarios: ' + err;
      this.setLoadingState(false);
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle load error
   */
  private onLoadError(): void {
    this.errorMessage = 'Error al cargar los usuarios. Por favor, intente nuevamente.';
    this.setLoadingState(false);
    this.cdr.markForCheck();
  }

  /**
   * Set loading state and clear errors
   */
  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
    if (isLoading) {
      this.errorMessage = '';
    }
  }

  /**
   * Update pagination config object
   */
  private updatePaginationConfig(): void {
    this.paginationConfig = {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalCount: this.totalCount,
      totalPages: Math.ceil(this.totalCount / this.pageSize)
    };
  }

  /**
   * Reload data and reset to first page
   */
  private reloadWithPaginationReset(): void {
    this.currentPage = 1;
    this.loadLegacyUsers();
  }

  // ============================================
  // PUBLIC FILTER HANDLERS (called from template)
  // ============================================

  /**
   * Search input handler
   */
  applyFilter(event: Event): void {
    const target = event.target as HTMLInputElement;
    const filterValue = (target?.value || '').trim().toLowerCase();
    this.searchSubject$.next(filterValue);
  }

  /**
   * Generic filter change handler
   */
  private onFilterChange(): void {
    this.reloadWithPaginationReset();
  }

  /**
   * Handle library level filter change
   */
  onLibraryLevelChange(value: string | null): void {
    this.filterService.setLibraryLevel(value);
    this.onFilterChange();
  }

  /**
   * Handle area filter change
   */
  onAreaFilterChange(value: string | null): void {
    this.filterService.setArea(value);
    this.onFilterChange();
  }

  /**
   * Handle district filter change
   */
  onDistritoFilterChange(value: string | null): void {
    this.filterService.setDistrito(value);
    this.onFilterChange();
  }

  /**
   * Handle community filter change
   */
  onComunidadFilterChange(value: string | null): void {
    this.filterService.setComunidad(value);
    this.onFilterChange();
  }

  /**
   * Clear all filters and reload
   */
  /**
   * Get filtered options for dropdown displays
   */
  getFilteredOptions(filterType: 'levels' | 'areas' | 'distritos' | 'comunidades'): string[] {
    const filterMap = {
      levels: () => this.filterService.getFilteredLevels(),
      areas: () => this.filterService.getFilteredAreas(),
      distritos: () => this.filterService.getFilteredDistritos(),
      comunidades: () => this.filterService.getFilteredComunidades()
    };
    return filterMap[filterType]();
  }

  // ============================================
  // PAGINATION HANDLERS (called from data-table)
  // ============================================

  /**
   * Handle page change event from data-table
   */
  onPageChange(event: { page: number }): void {
    this.currentPage = event.page;
    this.updatePaginationConfig();
    this.loadLegacyUsers();
  }

  /**
   * Handle page size change event from data-table
   */
  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.updatePaginationConfig();
    this.loadLegacyUsers();
  }

  createUser(memberId: string): void {
    this.router.navigate([`/members/${memberId}/new-user`]);
  }

  // ============================================
  // NAVIGATION
  // ============================================

  /**
   * Navigate to migration detail page with email parameter
   */
  onOpenMigrationModal(legacyUser: any): void {
    this.router.navigate(['/users/migration', legacyUser.email]);
  }
}

